import { useEffect, useState, FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import {
  PawPrint,
  Printer,
  CalendarCheck,
  Stethoscope,
  Syringe,
  Pill,
  FileText,
  Sparkles,
  Home,
  Footprints,
  GraduationCap,
  BedDouble,
  ClipboardList,
  Award,
  MapPin,
  Phone,
  Cake,
  BookOpen,
  Camera,
  X,
  Bell,
  Trash2,
  Send,
  Search,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { api, mensajeError } from "../api/client";
import { Spinner } from "../components/ui/Spinner";
import { redimensionarImagenABase64 } from "../lib/imagen";

/**
 * Portal PÚBLICO (sin login) del expediente de UNA mascota. A propósito NO
 * comparte layout, menú ni estilo con el panel de administración: es una
 * vista pensada para que el dueño de la mascota la consulte, la imprima, le
 * agregue fotos/recordatorios propios, y desde aquí gestione su relación con
 * el negocio (pedir eliminar su expediente o transferirlo a otro negocio de
 * la red) — nunca un panel de gestión del negocio.
 */

interface EventoExpediente {
  id: string;
  tipo: string;
  titulo: string;
  detalle?: string | null;
  fecha: string;
  estado?: string | null;
  responsable?: string | null;
  vacunaId?: string;
  certificadoDisponible?: boolean;
}

interface DatosExpediente {
  mascota: {
    nombre: string;
    especie: string;
    raza?: string | null;
    sexo: string;
    fechaNacimiento?: string | null;
    peso?: number | null;
    color?: string | null;
    esterilizado: boolean;
    fotoUrl?: string | null;
    clienteNombre: string;
    solicitudEliminacion: boolean;
  };
  // true cuando el negocio dio de baja a esta mascota — ya no tiene
  // veterinaria a cargo, aunque su expediente sigue existiendo.
  sinVeterinaria: boolean;
  negocio: {
    nombre: string;
    logoUrl?: string | null;
    colorPrimario?: string | null;
    direccion?: string | null;
    telefono?: string | null;
  };
  eventos: EventoExpediente[];
}

interface RazaInfo {
  nombre: string;
  descripcion?: string;
  temperamento?: string;
  esperanzaVida?: string;
  imagenUrl?: string;
  fuente: string;
}

interface Foto {
  id: string;
  url: string;
  createdAt: string;
}

interface RecordatorioCliente {
  id: string;
  mensaje: string;
  programadoPara: string;
  enviado: boolean;
}

interface ResultadoNegocio {
  id: string;
  nombre: string;
  tipoNegocio: string;
  direccion?: string | null;
}

interface SolicitudTransferencia {
  id: string;
  estado: "PENDIENTE" | "ACEPTADA" | "RECHAZADA" | "CANCELADA";
  createdAt: string;
  tenantDestino: { nombre: string };
}

interface CertificadoSaludResumen {
  id: string;
  fechaEmision: string;
}

const ICONOS_EVENTO: Record<string, any> = {
  Cita: CalendarCheck,
  Consulta: Stethoscope,
  Vacuna: Syringe,
  Receta: Pill,
  Documento: FileText,
  Estética: Sparkles,
  "A domicilio": Home,
  Paseo: Footprints,
  Entrenamiento: GraduationCap,
  Hospedaje: BedDouble,
};

const ETIQUETAS_ESTADO_TRANSFERENCIA: Record<string, string> = {
  PENDIENTE: "Pendiente de aprobación",
  ACEPTADA: "Aceptada",
  RECHAZADA: "Rechazada",
  CANCELADA: "Cancelada",
};

function calcularEdad(fechaNacimiento: string): string {
  const nacimiento = new Date(fechaNacimiento);
  const ahora = new Date();
  const dias = Math.floor((ahora.getTime() - nacimiento.getTime()) / (1000 * 60 * 60 * 24));
  if (dias < 31) return `${dias} día${dias === 1 ? "" : "s"}`;

  let años = ahora.getFullYear() - nacimiento.getFullYear();
  let meses = ahora.getMonth() - nacimiento.getMonth();
  if (ahora.getDate() < nacimiento.getDate()) meses--;
  if (meses < 0) {
    años--;
    meses += 12;
  }
  const partes: string[] = [];
  if (años > 0) partes.push(`${años} año${años === 1 ? "" : "s"}`);
  if (meses > 0) partes.push(`${meses} mes${meses === 1 ? "" : "es"}`);
  return partes.join(" y ") || "Menos de un mes";
}

// "YYYY-MM-DDTHH:mm" en hora local a partir de "ahora" (valor por defecto
// razonable para el input datetime-local del formulario de recordatorios).
function ahoraLocalInput(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ExpedientePublico() {
  const { token } = useParams();
  const [datos, setDatos] = useState<DatosExpediente | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [razaInfo, setRazaInfo] = useState<RazaInfo | null>(null);

  const [fotos, setFotos] = useState<Foto[]>([]);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [errorFotos, setErrorFotos] = useState<string | null>(null);

  const [recordatorios, setRecordatorios] = useState<RecordatorioCliente[]>([]);
  const [nuevoRecordatorio, setNuevoRecordatorio] = useState({ mensaje: "", programadoPara: ahoraLocalInput() });
  const [guardandoRecordatorio, setGuardandoRecordatorio] = useState(false);
  const [errorRecordatorio, setErrorRecordatorio] = useState<string | null>(null);

  const [busquedaNegocio, setBusquedaNegocio] = useState("");
  const [resultadosNegocio, setResultadosNegocio] = useState<ResultadoNegocio[]>([]);
  const [buscandoNegocio, setBuscandoNegocio] = useState(false);
  const [negocioSeleccionado, setNegocioSeleccionado] = useState<ResultadoNegocio | null>(null);
  const [enviandoTransferencia, setEnviandoTransferencia] = useState(false);
  const [errorTransferencia, setErrorTransferencia] = useState<string | null>(null);
  const [solicitudesTransferencia, setSolicitudesTransferencia] = useState<SolicitudTransferencia[]>([]);

  const [certificadosSalud, setCertificadosSalud] = useState<CertificadoSaludResumen[]>([]);

  const [mostrarConfirmEliminar, setMostrarConfirmEliminar] = useState(false);
  const [textoConfirmacion, setTextoConfirmacion] = useState("");
  const [enviandoEliminacion, setEnviandoEliminacion] = useState(false);
  const [errorEliminacion, setErrorEliminacion] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    api
      .get(`/portal-mascota/${token}`)
      .then((res) => setDatos(res.data))
      .catch((err) => setError(mensajeError(err)));
  }, [token]);

  useEffect(() => {
    if (!token || !datos?.mascota.raza) return;
    api
      .get(`/portal-mascota/${token}/raza-info`)
      .then((res) => setRazaInfo(res.data.raza))
      .catch(() => setRazaInfo(null));
  }, [token, datos?.mascota.raza]);

  useEffect(() => {
    if (!token) return;
    api.get(`/portal-mascota/${token}/fotos`).then((res) => setFotos(res.data.items ?? []));
    api.get(`/portal-mascota/${token}/recordatorios`).then((res) => setRecordatorios(res.data.items ?? []));
    api.get(`/portal-mascota/${token}/transferencias`).then((res) => setSolicitudesTransferencia(res.data.items ?? []));
    api.get(`/portal-mascota/${token}/certificados-salud`).then((res) => setCertificadosSalud(res.data.items ?? []));
  }, [token]);

  // Búsqueda de negocio con un pequeño debounce, para no disparar una
  // petición por cada tecla.
  useEffect(() => {
    if (!token || busquedaNegocio.trim().length < 2) {
      setResultadosNegocio([]);
      return;
    }
    setBuscandoNegocio(true);
    const timeout = setTimeout(() => {
      api
        .get(`/portal-mascota/${token}/buscar-negocio`, { params: { q: busquedaNegocio.trim() } })
        .then((res) => setResultadosNegocio(res.data.items ?? []))
        .finally(() => setBuscandoNegocio(false));
    }, 350);
    return () => clearTimeout(timeout);
  }, [token, busquedaNegocio]);

  async function subirFoto(e: React.ChangeEvent<HTMLInputElement>) {
    const archivo = e.target.files?.[0];
    e.target.value = "";
    if (!archivo || !token) return;
    setSubiendoFoto(true);
    setErrorFotos(null);
    try {
      const base64 = await redimensionarImagenABase64(archivo, 700);
      const res = await api.post(`/portal-mascota/${token}/fotos`, { url: base64 });
      setFotos((prev) => [res.data, ...prev]);
    } catch (err) {
      setErrorFotos(mensajeError(err));
    } finally {
      setSubiendoFoto(false);
    }
  }

  async function eliminarFoto(fotoId: string) {
    if (!token || !confirm("¿Eliminar esta foto?")) return;
    try {
      await api.delete(`/portal-mascota/${token}/fotos/${fotoId}`);
      setFotos((prev) => prev.filter((f) => f.id !== fotoId));
    } catch (err) {
      alert(mensajeError(err));
    }
  }

  async function crearRecordatorio(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setGuardandoRecordatorio(true);
    setErrorRecordatorio(null);
    try {
      const res = await api.post(`/portal-mascota/${token}/recordatorios`, {
        mensaje: nuevoRecordatorio.mensaje,
        programadoPara: new Date(nuevoRecordatorio.programadoPara).toISOString(),
      });
      setRecordatorios((prev) => [...prev, res.data].sort((a, b) => a.programadoPara.localeCompare(b.programadoPara)));
      setNuevoRecordatorio({ mensaje: "", programadoPara: ahoraLocalInput() });
    } catch (err) {
      setErrorRecordatorio(mensajeError(err));
    } finally {
      setGuardandoRecordatorio(false);
    }
  }

  async function eliminarRecordatorio(id: string) {
    if (!token || !confirm("¿Eliminar este recordatorio?")) return;
    try {
      await api.delete(`/portal-mascota/${token}/recordatorios/${id}`);
      setRecordatorios((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      alert(mensajeError(err));
    }
  }

  async function enviarTransferencia() {
    if (!token || !negocioSeleccionado) return;
    setEnviandoTransferencia(true);
    setErrorTransferencia(null);
    try {
      await api.post(`/portal-mascota/${token}/transferir`, { tenantDestinoId: negocioSeleccionado.id });
      const res = await api.get(`/portal-mascota/${token}/transferencias`);
      setSolicitudesTransferencia(res.data.items ?? []);
      setNegocioSeleccionado(null);
      setBusquedaNegocio("");
      setResultadosNegocio([]);
    } catch (err) {
      setErrorTransferencia(mensajeError(err));
    } finally {
      setEnviandoTransferencia(false);
    }
  }

  async function solicitarEliminacion(e: FormEvent) {
    e.preventDefault();
    if (!token || !datos) return;
    setEnviandoEliminacion(true);
    setErrorEliminacion(null);
    try {
      await api.post(`/portal-mascota/${token}/solicitar-eliminacion`, { confirmacion: textoConfirmacion });
      setDatos({ ...datos, mascota: { ...datos.mascota, solicitudEliminacion: true } });
      setMostrarConfirmEliminar(false);
      setTextoConfirmacion("");
    } catch (err) {
      setErrorEliminacion(mensajeError(err));
    } finally {
      setEnviandoEliminacion(false);
    }
  }

  const colorAcento = datos && !datos.sinVeterinaria ? datos.negocio.colorPrimario || "#D97706" : "#D97706";

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-amber-50 to-orange-50 px-4">
        <div className="max-w-sm rounded-3xl border border-amber-100 bg-white p-8 text-center shadow-sm">
          <PawPrint size={32} className="mx-auto mb-3 text-amber-400" />
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!datos) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-amber-50 to-orange-50">
        <Spinner className="h-8 w-8 text-amber-500" />
      </div>
    );
  }

  const { mascota, negocio, eventos, sinVeterinaria } = datos;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-white pb-16 print:bg-white print:pb-0">
      <style>{`
        @media print {
          .no-imprimir { display: none !important; }
          body { background: white !important; }
        }
      `}</style>

      <div className="mx-auto max-w-2xl px-4 pt-10 print:pt-0">
        {/* Encabezado del negocio (o aviso de que ya no tiene uno) */}
        {sinVeterinaria ? (
          <div className="mb-6 flex flex-col items-center text-center">
            <div className="mb-3 flex h-16 w-16 items-center justify-center overflow-hidden rounded-3xl bg-slate-200 text-slate-500 shadow-md">
              <PawPrint size={28} />
            </div>
            <p className="font-display text-2xl text-slate-600">Sin veterinaria registrada</p>
            <p className="mt-1 max-w-sm text-xs text-slate-400">
              Este expediente ya no está a cargo de ningún negocio, pero el historial de {mascota.nombre} se conserva
              completo aquí abajo.
            </p>
          </div>
        ) : (
          <div className="mb-6 flex flex-col items-center text-center">
            <div
              className="mb-3 flex h-16 w-16 items-center justify-center overflow-hidden rounded-3xl text-white shadow-md"
              style={{ backgroundColor: colorAcento }}
            >
              {negocio.logoUrl ? (
                <img src={negocio.logoUrl} alt={negocio.nombre} className="h-full w-full object-cover" />
              ) : (
                <PawPrint size={28} />
              )}
            </div>
            <p className="font-display text-2xl text-slate-800">{negocio.nombre}</p>
            {(negocio.direccion || negocio.telefono) && (
              <p className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-slate-400">
                {negocio.direccion && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={12} /> {negocio.direccion}
                  </span>
                )}
                {negocio.telefono && (
                  <span className="inline-flex items-center gap-1">
                    <Phone size={12} /> {negocio.telefono}
                  </span>
                )}
              </p>
            )}
          </div>
        )}

        {/* Ficha de la mascota */}
        <div className="mb-6 overflow-hidden rounded-3xl border border-amber-100 bg-white shadow-sm">
          <div className="flex flex-wrap items-center gap-4 p-6">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl bg-amber-50 text-amber-400 ring-4 ring-amber-50">
              {mascota.fotoUrl ? (
                <img src={mascota.fotoUrl} alt={mascota.nombre} className="h-full w-full object-cover" />
              ) : (
                <PawPrint size={32} />
              )}
            </div>
            <div className="flex-1">
              <h1 className="font-display text-3xl text-slate-800">{mascota.nombre}</h1>
              <p className="text-sm text-slate-400">
                {mascota.especie} · {mascota.raza || "Sin raza especificada"} · {mascota.sexo}
              </p>
              <p className="text-sm text-slate-400">Tutor(a): {mascota.clienteNombre}</p>
            </div>
            <button
              onClick={() => window.print()}
              className="no-imprimir inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white shadow-sm"
              style={{ backgroundColor: colorAcento }}
            >
              <Printer size={15} /> Imprimir
            </button>
          </div>

          <div className="flex flex-wrap gap-2 border-t border-amber-50 px-6 py-4">
            {mascota.fechaNacimiento && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700">
                <Cake size={13} /> {calcularEdad(mascota.fechaNacimiento)}
              </span>
            )}
            {mascota.peso && (
              <span className="rounded-full bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">{mascota.peso} kg</span>
            )}
            {mascota.color && (
              <span className="rounded-full bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">{mascota.color}</span>
            )}
            <span className="rounded-full bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
              {mascota.esterilizado ? "Esterilizado(a)" : "No esterilizado(a)"}
            </span>
          </div>
        </div>

        {/* Información general de la raza */}
        {razaInfo && (
          <div className="mb-6 rounded-3xl border border-amber-100 bg-white p-6 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <BookOpen size={18} className="text-amber-500" />
              <h2 className="font-display text-lg text-slate-800">Sobre la raza {razaInfo.nombre}</h2>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row">
              {razaInfo.imagenUrl && (
                <img src={razaInfo.imagenUrl} alt={razaInfo.nombre} className="h-32 w-32 shrink-0 rounded-2xl object-cover" />
              )}
              <div className="flex-1 space-y-1.5 text-sm text-slate-600">
                {razaInfo.temperamento && (
                  <p>
                    <strong className="text-slate-700">Temperamento:</strong> {razaInfo.temperamento}
                  </p>
                )}
                {razaInfo.esperanzaVida && (
                  <p>
                    <strong className="text-slate-700">Esperanza de vida:</strong> {razaInfo.esperanzaVida} años
                  </p>
                )}
                {razaInfo.descripcion && <p>{razaInfo.descripcion}</p>}
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-300">
              Fuente: {razaInfo.fuente} · información general de la raza, no sustituye el diagnóstico de tu veterinario.
            </p>
          </div>
        )}

        {/* Línea de tiempo del expediente */}
        <div className="mb-3 flex items-center gap-2 px-1">
          <ClipboardList size={18} className="text-amber-500" />
          <h2 className="font-display text-lg text-slate-800">Expediente completo</h2>
        </div>

        {eventos.length === 0 ? (
          <div className="mb-6 rounded-3xl border border-amber-100 bg-white p-6 text-center text-sm text-slate-400">
            Todavía no hay actividades registradas.
          </div>
        ) : (
          <ol className="relative mb-6 border-l border-amber-200 pl-6">
            {eventos.map((ev) => {
              const Icono = ICONOS_EVENTO[ev.tipo] ?? ClipboardList;
              return (
                <li key={ev.id} className="mb-5 last:mb-0">
                  <span className="absolute -left-[15px] flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-amber-600 ring-4 ring-amber-50">
                    <Icono size={14} />
                  </span>
                  <div className="rounded-2xl border border-amber-100 bg-white p-4 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-amber-500">{ev.tipo}</p>
                        <p className="font-medium text-slate-800">{ev.titulo}</p>
                      </div>
                      <span className="whitespace-nowrap text-xs text-slate-400">
                        {new Date(ev.fecha).toLocaleDateString("es-MX", { dateStyle: "medium" })}
                      </span>
                    </div>
                    {ev.detalle && <p className="mt-2 text-sm text-slate-600">{ev.detalle}</p>}
                    {ev.responsable && <p className="mt-1 text-xs text-slate-400">Atendió: {ev.responsable}</p>}
                    {ev.tipo === "Vacuna" && ev.certificadoDisponible && ev.vacunaId && (
                      <Link
                        to={`/portal-mascota/${token}/vacuna/${ev.vacunaId}/certificado`}
                        className="no-imprimir mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-amber-600 hover:underline"
                      >
                        <Award size={14} /> Ver certificado de vacunación
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {/* Certificados de salud emitidos */}
        {certificadosSalud.length > 0 && (
          <div className="no-imprimir mb-6 rounded-3xl border border-amber-100 bg-white p-6 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <ShieldCheck size={18} className="text-amber-500" />
              <h2 className="font-display text-lg text-slate-800">Certificados de salud</h2>
            </div>
            <ul className="space-y-2">
              {certificadosSalud.map((c) => (
                <li key={c.id} className="flex items-center justify-between rounded-xl border border-amber-50 bg-amber-50/40 px-3 py-2 text-sm">
                  <span className="text-slate-600">Emitido el {new Date(c.fechaEmision).toLocaleDateString("es-MX", { dateStyle: "long" })}</span>
                  <Link
                    to={`/portal-mascota/${token}/certificado-salud/${c.id}`}
                    className="inline-flex items-center gap-1.5 font-medium text-amber-600 hover:underline"
                  >
                    <Award size={14} /> Ver / imprimir
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Galería de fotos del cliente */}
        <div className="no-imprimir mb-6 rounded-3xl border border-amber-100 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Camera size={18} className="text-amber-500" />
              <h2 className="font-display text-lg text-slate-800">Fotos de {mascota.nombre}</h2>
            </div>
            <label
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium text-white shadow-sm"
              style={{ backgroundColor: colorAcento }}
            >
              <input type="file" accept="image/*" className="hidden" onChange={subirFoto} disabled={subiendoFoto} />
              <Camera size={13} /> {subiendoFoto ? "Subiendo..." : "Agregar foto"}
            </label>
          </div>
          {errorFotos && <p className="mb-3 text-sm text-red-600">{errorFotos}</p>}
          {fotos.length === 0 ? (
            <p className="text-sm text-slate-400">Todavía no has agregado fotos.</p>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {fotos.map((f) => (
                <div key={f.id} className="group relative aspect-square overflow-hidden rounded-2xl bg-amber-50">
                  <img src={f.url} alt="" className="h-full w-full object-cover" />
                  <button
                    onClick={() => eliminarFoto(f.id)}
                    className="absolute right-1 top-1 rounded-full bg-black/50 p-1 text-white opacity-0 transition group-hover:opacity-100"
                    title="Eliminar foto"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recordatorios personales del cliente */}
        <div className="no-imprimir mb-6 rounded-3xl border border-amber-100 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <Bell size={18} className="text-amber-500" />
            <h2 className="font-display text-lg text-slate-800">Mis recordatorios</h2>
          </div>
          <p className="mb-4 text-sm text-slate-500">
            Crea recordatorios propios para {mascota.nombre} (ej. dar medicina, bañarlo) y te llegará un aviso por WhatsApp o
            correo en la fecha y hora que elijas.
          </p>

          <form onSubmit={crearRecordatorio} className="mb-4 flex flex-col gap-2 sm:flex-row">
            <input
              className="flex-1 rounded-xl border border-amber-100 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none"
              placeholder="Ej. Dar medicina a las 3pm"
              required
              maxLength={300}
              value={nuevoRecordatorio.mensaje}
              onChange={(e) => setNuevoRecordatorio({ ...nuevoRecordatorio, mensaje: e.target.value })}
            />
            <input
              type="datetime-local"
              className="rounded-xl border border-amber-100 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none"
              required
              value={nuevoRecordatorio.programadoPara}
              onChange={(e) => setNuevoRecordatorio({ ...nuevoRecordatorio, programadoPara: e.target.value })}
            />
            <button
              type="submit"
              disabled={guardandoRecordatorio}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium text-white shadow-sm"
              style={{ backgroundColor: colorAcento }}
            >
              <Bell size={14} /> {guardandoRecordatorio ? "Creando..." : "Crear"}
            </button>
          </form>
          {errorRecordatorio && <p className="mb-3 text-sm text-red-600">{errorRecordatorio}</p>}

          {recordatorios.length === 0 ? (
            <p className="text-sm text-slate-400">No tienes recordatorios creados.</p>
          ) : (
            <ul className="space-y-2">
              {recordatorios.map((r) => (
                <li
                  key={r.id}
                  className="flex items-center justify-between gap-2 rounded-xl border border-amber-50 bg-amber-50/40 px-3 py-2 text-sm"
                >
                  <div>
                    <p className="text-slate-700">{r.mensaje}</p>
                    <p className="flex items-center gap-1 text-xs text-slate-400">
                      <Clock size={11} /> {new Date(r.programadoPara).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}
                      {r.enviado && (
                        <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-600">
                          <CheckCircle2 size={11} /> Enviado
                        </span>
                      )}
                    </p>
                  </div>
                  <button onClick={() => eliminarRecordatorio(r.id)} className="text-slate-300 hover:text-red-500" title="Eliminar">
                    <Trash2 size={15} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Transferir expediente a otro negocio */}
        <div className="no-imprimir mb-6 rounded-3xl border border-amber-100 bg-white p-6 shadow-sm">
          <div className="mb-2 flex items-center gap-2">
            <Send size={18} className="text-amber-500" />
            <h2 className="font-display text-lg text-slate-800">Transferir expediente a otro negocio</h2>
          </div>
          <p className="mb-4 text-sm text-slate-500">
            {sinVeterinaria
              ? `¿${mascota.nombre} ya tiene una nueva veterinaria dentro de la red de Mascotas Vet? Busca su nombre y solicita que le abran un expediente ahí con su historial.`
              : `¿${mascota.nombre} también es paciente de otro negocio de la red de Mascotas Vet? Busca su nombre y solicita una copia del perfil básico allá — el negocio destino debe aceptar la solicitud, y ${negocio.nombre} conserva su expediente tal como está.`}
          </p>

          {negocioSeleccionado ? (
            <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4">
              <p className="text-sm text-slate-700">
                Vas a solicitar transferir el expediente a <strong>{negocioSeleccionado.nombre}</strong>
                {negocioSeleccionado.direccion ? ` (${negocioSeleccionado.direccion})` : ""}.
              </p>
              {errorTransferencia && <p className="mt-2 text-sm text-red-600">{errorTransferencia}</p>}
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => setNegocioSeleccionado(null)}
                  className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600"
                >
                  Cancelar
                </button>
                <button
                  onClick={enviarTransferencia}
                  disabled={enviandoTransferencia}
                  className="rounded-xl px-4 py-1.5 text-xs font-medium text-white shadow-sm"
                  style={{ backgroundColor: colorAcento }}
                >
                  {enviandoTransferencia ? "Enviando..." : "Confirmar solicitud"}
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
              <input
                className="w-full rounded-xl border border-amber-100 py-2 pl-9 pr-3 text-sm focus:border-amber-300 focus:outline-none"
                placeholder="Escribe el nombre del negocio..."
                value={busquedaNegocio}
                onChange={(e) => setBusquedaNegocio(e.target.value)}
              />
              {busquedaNegocio.trim().length >= 2 && (
                <div className="mt-2 overflow-hidden rounded-xl border border-amber-100">
                  {buscandoNegocio ? (
                    <p className="p-3 text-sm text-slate-400">Buscando...</p>
                  ) : resultadosNegocio.length === 0 ? (
                    <p className="p-3 text-sm text-slate-400">No encontramos negocios con ese nombre.</p>
                  ) : (
                    resultadosNegocio.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => {
                          setNegocioSeleccionado(n);
                          setResultadosNegocio([]);
                        }}
                        className="block w-full border-b border-amber-50 px-3 py-2 text-left text-sm last:border-0 hover:bg-amber-50"
                      >
                        <span className="font-medium text-slate-700">{n.nombre}</span>
                        {n.direccion && <span className="ml-1 text-xs text-slate-400">· {n.direccion}</span>}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {solicitudesTransferencia.length > 0 && (
            <ul className="mt-4 space-y-1.5 border-t border-amber-50 pt-4">
              {solicitudesTransferencia.map((s) => (
                <li key={s.id} className="flex items-center justify-between text-xs text-slate-500">
                  <span>
                    A <strong className="text-slate-600">{s.tenantDestino.nombre}</strong>
                  </span>
                  <span
                    className={
                      s.estado === "ACEPTADA"
                        ? "text-emerald-600"
                        : s.estado === "RECHAZADA"
                        ? "text-red-500"
                        : "text-amber-600"
                    }
                  >
                    {ETIQUETAS_ESTADO_TRANSFERENCIA[s.estado]}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Solicitar eliminación del expediente — no aplica si ya no hay
            ningún negocio a cargo que pueda revisar la solicitud. */}
        {!sinVeterinaria && (
        <div className="no-imprimir mb-6 rounded-3xl border border-red-100 bg-white p-6 shadow-sm">
          <div className="mb-2 flex items-center gap-2">
            <ShieldAlert size={18} className="text-red-400" />
            <h2 className="font-display text-lg text-slate-800">Eliminar expediente</h2>
          </div>

          {mascota.solicitudEliminacion ? (
            <p className="flex items-center gap-2 text-sm text-amber-700">
              <AlertTriangle size={15} /> Ya enviaste esta solicitud — {negocio.nombre} la está revisando.
            </p>
          ) : !mostrarConfirmEliminar ? (
            <>
              <p className="mb-3 text-sm text-slate-500">
                Puedes solicitar que {negocio.nombre} elimine el expediente de {mascota.nombre}. Esto no borra nada de
                inmediato: se le avisa al negocio y es el veterinario quien decide y ejecuta la baja.
              </p>
              <button
                onClick={() => setMostrarConfirmEliminar(true)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <Trash2 size={14} /> Solicitar eliminación del expediente
              </button>
            </>
          ) : (
            <form onSubmit={solicitarEliminacion} className="space-y-3">
              <p className="text-sm text-slate-600">
                Para confirmar, escribe exactamente el nombre de tu mascota: <strong>{mascota.nombre}</strong>
              </p>
              <input
                className="w-full rounded-xl border border-red-200 px-3 py-2 text-sm focus:border-red-300 focus:outline-none"
                value={textoConfirmacion}
                onChange={(e) => setTextoConfirmacion(e.target.value)}
                autoFocus
              />
              {errorEliminacion && <p className="text-sm text-red-600">{errorEliminacion}</p>}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setMostrarConfirmEliminar(false);
                    setTextoConfirmacion("");
                    setErrorEliminacion(null);
                  }}
                  className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={enviandoEliminacion || textoConfirmacion.trim().toLowerCase() !== mascota.nombre.trim().toLowerCase()}
                  className="rounded-xl bg-red-500 px-4 py-1.5 text-xs font-medium text-white shadow-sm disabled:opacity-40"
                >
                  {enviandoEliminacion ? "Enviando..." : "Confirmar solicitud"}
                </button>
              </div>
            </form>
          )}
        </div>
        )}

        <p className="no-imprimir mt-8 text-center text-xs text-slate-300">
          {sinVeterinaria ? "Solo tú puedes ver este enlace" : `Expediente generado por ${negocio.nombre} · Solo tú puedes ver este enlace`}
        </p>
      </div>
    </div>
  );
}
